import { apiService } from './api';

export interface AWSCredential {
  id: number;
  credentialName: string;
  region: string;
  isDefault: boolean;
  isActive: boolean;
  lastUsed?: string;
  lastSync?: string;
}

export interface AddCredentialRequest {
  credentialName: string;
  accessKeyId: string;
  secretAccessKey: string;
  region: string;
  isDefault?: boolean;
}

export interface TestCredentialResponse {
  success: boolean;
  accountId?: string;
  arn?: string;
  userId?: string;
  error?: string;
}

export interface ResourceSummary {
  ec2: {
    instances: number;
    instanceTypes: number;
    regions: number;
    runningInstances: number;
    totalVcpus: number;
    totalMemory: number;
    monthlyComputeCost: number;
  };
  ebs: {
    volumes: number;
    totalGiB: number;
    monthlyCost: number;
  };
  s3: {
    buckets: number;
    totalGiB: number;
    objects: number;
    monthlyCost: number;
    standardGiB?: number;
    standardIAGiB?: number;
    oneZoneIAGiB?: number;
    glacierGiB?: number;
    glacierDeepArchiveGiB?: number;
    intelligentTieringGiB?: number;
  };
  eks: {
    clusters: number;
    nodeGroups: number;
    totalNodes: number;
    totalVcpus: number;
    totalMemory: number;
    monthlyComputeCost: number;
  };
  rds: {
    instances: number;
    instanceClasses: number;
    engines: number;
    regions: number;
    availableInstances: number;
    totalVcpus: number;
    totalMemory: number;
    totalStorageGB: number;
    monthlyComputeCost: number;
    monthlyStorageCost: number;
    totalMonthlyCost: number;
  };
  fsx?: {
    fileSystems: number;
    types: number;
    totalStorageGB: number;
    monthlyCost: number;
  };
  efs?: {
    fileSystems: number;
    totalGiB: number;
    totalMountTargets: number;
    monthlyCost: number;
  };
  dynamodb?: {
    tables: number;
    totalGiB: number;
    totalItems: number;
    monthlyCost: number;
  };
  cloudwatchLogs?: {
    logGroups: number;
    totalStoredGiB: number;
    totalIncomingGB: number;
    monthlyCost: number;
  };
  ecs?: {
    clusters: number;
    services: number;
    runningTasks: number;
    runningCount: number;
    desiredCount: number;
    monthlyCost: number;
  };
  cloudfront?: {
    distributions: number;
    enabledDistributions: number;
    totalMonthlyDataTransferGB: number;
    totalMonthlyRequests: number;
    monthlyCost: number;
  };
  vpc?: {
    vpcs: number;
    customVpcs: number;
    totalSubnets: number;
    totalNatGateways: number;
    totalVpnConnections: number;
    totalInternetGateways: number;
    monthlyCost: number;
  };
  lambda?: {
    functions: number;
    runtimes: number;
    regions: number;
    totalCodeSize: number;
    totalInvocations: number;
    avgDuration: number;
    monthlyCost: number;
  };
  timestamp: string;
}

export interface CostAnalysis {
  compute: Array<{
    region: string;
    instanceType: string;
    count: number;
    avgHourlyCost: number;
    monthlyEstimate: number;
  }>;
  storage: {
    ebs: Array<{
      region: string;
      volumeType: string;
      monthlyCost: number;
      totalGiB: number;
      volumeCount: number;
    }>;
    s3: Array<{
      region: string;
      monthlyCost: number;
      totalGiB: number;
      totalObjects: number;
      bucketCount: number;
    }>;
    rds?: Array<{
      region: string;
      volumeType: string;
      monthlyCost: number;
      totalGiB: number;
      volumeCount: number;
    }>;
  };
  networking?: Array<{
    region: string;
    monthlyCost: number;
    natGateways: number;
    vpnConnections: number;
    vpcCount: number;
  }>;
  lambda?: Array<{
    region: string;
    runtime: string;
    monthlyCost: number;
    totalInvocations: number;
    functionCount: number;
  }>;
  timestamp: string;
}

export interface SyncResponse {
  message: string;
  credentialId: string;
  regions: string;
}

export interface SyncStatusResponse {
  credentials: Array<{
    id: number;
    credentialName: string;
    lastSync: string | null;
    isActive: boolean;
  }>;
}

export interface FSxFileSystem {
  id: number;
  fileSystemId: string;
  fileSystemType: string;
  storageCapacity: number;
  region: string;
  lifecycleState: string;
  monthlyCost: number;
}

export interface EFSFileSystem {
  id: number;
  fileSystemId: string;
  name?: string;
  sizeInBytes: number;
  performanceMode: string;
  throughputMode: string;
  region: string;
  monthlyCost: number;
}

export interface DynamoDBTable {
  id: number;
  tableName: string;
  billingMode: string;
  itemCount: number;
  sizeBytes: number;
  region: string;
  monthlyCost: number;
}

export interface CloudWatchLogGroup {
  id: number;
  logGroupName: string;
  region: string;
  storedBytes: number;
  monthlyIncomingGB: number;
  monthlyCost: number;
}

export interface CloudFrontDistribution {
  id: number;
  distributionId: string;
  domainName: string;
  status: string;
  enabled: boolean;
  priceClass: string;
  httpVersion: string;
  comment?: string;
  aliases?: string[];
  origins?: Array<{
    domainName: string;
    originPath?: string;
    s3OriginConfig?: any;
    customOriginConfig?: any;
  }>;
  defaultRootObject?: string;
  viewerCertificate?: {
    cloudFrontDefaultCertificate?: boolean;
    acmCertificateArn?: string;
    sslSupportMethod?: string;
  };
  region: string;
  monthlyDataTransferGB: number;
  monthlyHttpRequests: number;
  monthlyHttpsRequests: number;
  totalMonthlyCost: number;
}

export interface VPC {
  id: number;
  vpcId: string;
  name?: string;
  cidrBlock: string;
  region: string;
  state: string;
  isDefault: boolean;
  subnetCount: number;
  internetGatewayCount: number;
  natGatewayCount: number;
  vpnConnectionCount: number;
  monthlyCost: number;
}

export interface LambdaFunction {
  id: number;
  functionName: string;
  functionArn: string;
  runtime: string;
  handler: string;
  codeSize: number;
  memorySize: number;
  timeout: number;
  lastModified: string;
  version: string;
  description?: string;
  region: string;
  monthlyCost: number;
  invocations: number;
  averageDuration: number;
}

export const awsService = {
  // Credentials Management
  async getCredentials(): Promise<{ credentials: AWSCredential[] }> {
    const { data } = await apiService.get<{ credentials: AWSCredential[] }>('/aws-credentials');
    return data;
  },

  async addCredential(credential: AddCredentialRequest): Promise<{ credential: AWSCredential }> {
    const { data } = await apiService.post<{ credential: AWSCredential }>('/aws-credentials', credential);
    return data;
  },

  async updateCredential(id: number, updates: Partial<AWSCredential>): Promise<{ credential: AWSCredential }> {
    const { data } = await apiService.put<{ credential: AWSCredential }>(`/aws-credentials/${id}`, updates);
    return data;
  },

  async deleteCredential(id: number, force: boolean = false): Promise<any> {
    const url = force ? `/aws-credentials/${id}?force=true` : `/aws-credentials/${id}`;
    const { data } = await apiService.delete<any>(url);
    return data;
  },

  async testCredential(id: number): Promise<TestCredentialResponse> {
    const { data } = await apiService.post<TestCredentialResponse>(`/aws-credentials/${id}/test`);
    return data;
  },

  // Resource Synchronization
  async syncAllResources(credentialId: number, regions?: string[]): Promise<SyncResponse> {
    const { data } = await apiService.post<SyncResponse>(`/aws-sync/credentials/${credentialId}/sync`, {
      regions
    }, {
      timeout: 180000 // 3 minutes timeout for resource sync operations
    });
    return data;
  },

  // Unified sync - syncs both resources and bandwidth
  async unifiedSync(credentialId: number, regions?: string[]): Promise<any> {
    const { data } = await apiService.post(`/aws-sync/credentials/${credentialId}/unified-sync`, {
      regions
    }, {
      timeout: 300000 // 5 minutes timeout for unified sync operations
    });
    return data;
  },

  async syncResourceType(credentialId: number, resourceType: string, regions?: string[]): Promise<any> {
    const { data } = await apiService.post(`/aws-sync/credentials/${credentialId}/sync-type`, {
      resourceType,
      regions
    }, {
      timeout: 120000 // 2 minutes timeout for specific resource type sync
    });
    return data;
  },

  async getSyncStatus(): Promise<SyncStatusResponse> {
    const { data } = await apiService.get<SyncStatusResponse>('/aws-sync/sync-status');
    return data;
  },

  // Resource Data
  async getResourceSummary(): Promise<ResourceSummary> {
    const { data } = await apiService.get<ResourceSummary>('/aws/summary');
    return data;
  },

  async getCostAnalysis(): Promise<CostAnalysis> {
    const { data } = await apiService.get<CostAnalysis>('/aws/cost-analysis');
    return data;
  },

  async getEC2Instances(filters?: { region?: string; state?: string; instanceType?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.region) params.append('region', filters.region);
    if (filters?.state) params.append('state', filters.state);
    if (filters?.instanceType) params.append('instanceType', filters.instanceType);
    
    const { data } = await apiService.get(`/aws/ec2-instances?${params.toString()}`);
    return data;
  },

  async getEBSVolumes(filters?: { region?: string; state?: string; volumeType?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.region) params.append('region', filters.region);
    if (filters?.state) params.append('state', filters.state);
    if (filters?.volumeType) params.append('volumeType', filters.volumeType);
    
    const { data } = await apiService.get(`/aws/ebs-volumes?${params.toString()}`);
    return data;
  },

  async getS3Buckets(filters?: { region?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.region) params.append('region', filters.region);
    
    const { data } = await apiService.get(`/aws/s3-buckets?${params.toString()}`);
    return data;
  },

  async getEKSClusters(filters?: { region?: string; status?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.region) params.append('region', filters.region);
    if (filters?.status) params.append('status', filters.status);
    
    const { data } = await apiService.get(`/aws/eks-clusters?${params.toString()}`);
    return data;
  },

  // Instance Types (public endpoint)
  async getInstanceTypes(filters?: {
    region?: string;
    family?: string;
    minVcpus?: number;
    maxVcpus?: number;
    minMemory?: number;
    maxMemory?: number;
    instanceType?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const { data } = await apiService.get(`/aws/instance-types?${params.toString()}`);
    return data;
  },

  // RDS Instances
  async getRDSInstances(filters?: { region?: string; engine?: string; dbInstanceStatus?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.region) params.append('region', filters.region);
    if (filters?.engine) params.append('engine', filters.engine);
    if (filters?.dbInstanceStatus) params.append('dbInstanceStatus', filters.dbInstanceStatus);
    
    const { data } = await apiService.get(`/aws/rds-instances?${params.toString()}`);
    return data;
  },

  // FSx File Systems
  async getFSxFileSystems(filters?: { region?: string; fileSystemType?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.region) params.append('region', filters.region);
    if (filters?.fileSystemType) params.append('fileSystemType', filters.fileSystemType);
    
    const { data } = await apiService.get(`/aws/fsx-filesystems?${params.toString()}`);
    return data;
  },

  // EFS File Systems
  async getEFSFileSystems(filters?: { region?: string; storageClass?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.region) params.append('region', filters.region);
    if (filters?.storageClass) params.append('storageClass', filters.storageClass);
    
    const { data } = await apiService.get(`/aws/efs-filesystems?${params.toString()}`);
    return data;
  },

  // DynamoDB Tables
  async getDynamoDBTables(filters?: { region?: string; billingMode?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.region) params.append('region', filters.region);
    if (filters?.billingMode) params.append('billingMode', filters.billingMode);
    
    const { data } = await apiService.get(`/aws/dynamodb-tables?${params.toString()}`);
    return data;
  },

  // CloudWatch Log Groups
  async getCloudWatchLogGroups(filters?: { region?: string; retentionInDays?: number }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.region) params.append('region', filters.region);
    if (filters?.retentionInDays) params.append('retentionInDays', filters.retentionInDays.toString());
    
    const { data } = await apiService.get(`/aws/cloudwatch-loggroups?${params.toString()}`);
    return data;
  },

  // ECS Resources
  async getECSClusters(filters?: { region?: string; status?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.region) params.append('region', filters.region);
    if (filters?.status) params.append('status', filters.status);
    
    const { data } = await apiService.get(`/aws/ecs-clusters?${params.toString()}`);
    return data;
  },

  // CloudFront Distributions
  async getCloudFrontDistributions(filters?: { status?: string; enabled?: boolean }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.enabled !== undefined) params.append('enabled', filters.enabled.toString());
    
    const { data } = await apiService.get(`/aws/cloudfront-distributions?${params.toString()}`);
    return data;
  },

  // VPCs
  async getVPCs(filters?: { region?: string; isDefault?: boolean }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.region) params.append('region', filters.region);
    if (filters?.isDefault !== undefined) params.append('isDefault', filters.isDefault.toString());
    
    const { data } = await apiService.get(`/aws/vpcs?${params.toString()}`);
    return data;
  },

  // Lambda Functions
  async getLambdaFunctions(filters?: { region?: string; runtime?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.region) params.append('region', filters.region);
    if (filters?.runtime) params.append('runtime', filters.runtime);
    
    const { data } = await apiService.get(`/aws/lambda-functions?${params.toString()}`);
    return data;
  }
};

export default awsService;